import React, { useEffect, useState } from 'react'

export interface ToastMessage {
  id: string
  type: 'success' | 'error' | 'info'
  title: string
  message?: string
  duration?: number
}

interface ToastProps {
  message: ToastMessage
  onClose: (id: string) => void
}

const Toast: React.FC<ToastProps> = ({ message, onClose }) => {
  const [isVisible, setIsVisible] = useState(false)
  const [isLeaving, setIsLeaving] = useState(false)

  useEffect(() => {
    // Анимация появления
    const showTimer = setTimeout(() => setIsVisible(true), 10)
    
    // Автоматическое скрытие
    const hideTimer = setTimeout(() => {
      setIsLeaving(true)
      setTimeout(() => onClose(message.id), 300)
    }, message.duration || 3000)

    return () => {
      clearTimeout(showTimer)
      clearTimeout(hideTimer)
    }
  }, [message.id, message.duration, onClose])

  const getTypeStyles = () => {
    switch (message.type) {
      case 'success':
        return {
          bg: 'bg-green-50 border-green-200 border-l-green-500',
          icon: 'text-green-600',
          iconName: 'checkmark-circle'
        }
      case 'error':
        return {
          bg: 'bg-red-50 border-red-200 border-l-red-500',
          icon: 'text-red-600',
          iconName: 'alert-circle'
        }
      case 'info':
      default:
        return {
          bg: 'bg-blue-50 border-blue-200 border-l-blue-500',
          icon: 'text-blue-600',
          iconName: 'information-circle'
        }
    }
  }

  const styles = getTypeStyles()

  return (
    <div
      className={`
        fixed top-4 right-4 left-4 sm:left-auto z-50 sm:w-96
        transform transition-all duration-300 ease-out
        ${isVisible && !isLeaving 
          ? 'translate-x-0 opacity-100' 
          : 'translate-x-full opacity-0'
        }
      `}
    >
      <div className={`
        ${styles.bg} border rounded-lg shadow-xl p-4
        backdrop-blur-sm border-l-4
      `}>
        <div className="flex items-start gap-3">
          <ion-icon 
            name={styles.iconName}
            className={`${styles.icon} text-lg flex-shrink-0 mt-0.5`}
          ></ion-icon>
          
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900">
              {message.title}
            </p>
            {message.message && (
              <p className="text-sm text-gray-600 mt-1">
                {message.message}
              </p>
            )}
          </div>
          
          <button
            onClick={() => onClose(message.id)}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <ion-icon name="close" className="text-sm"></ion-icon>
          </button>
        </div>
      </div>
    </div>
  )
}

// Toast Manager для управления несколькими уведомлениями
interface ToastManagerProps {
  messages: ToastMessage[]
  onClose: (id: string) => void
}

export const ToastManager: React.FC<ToastManagerProps> = ({ messages, onClose }) => {
  return (
    <div className="fixed top-0 left-0 right-0 z-50 pointer-events-none">
      <div className="p-4 space-y-3">
        {messages.map((message, index) => (
          <div
            key={message.id}
            className="pointer-events-auto"
            style={{ 
              zIndex: 50 - index
            }}
          >
            <Toast message={message} onClose={onClose} />
          </div>
        ))}
      </div>
    </div>
  )
}

// Хук для управления Toast уведомлениями
export function useToast() {
  const [messages, setMessages] = useState<ToastMessage[]>([])

  const showToast = (toast: Omit<ToastMessage, 'id'>) => {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9)
    const newMessage: ToastMessage = { ...toast, id }
    
    setMessages(prev => [...prev, newMessage])
  }

  const hideToast = (id: string) => {
    setMessages(prev => prev.filter(msg => msg.id !== id))
  }

  const showSuccess = (title: string, message?: string) => {
    showToast({ type: 'success', title, message })
  }

  const showError = (title: string, message?: string) => {
    showToast({ type: 'error', title, message })
  }

  const showInfo = (title: string, message?: string) => {
    showToast({ type: 'info', title, message })
  }

  return {
    messages,
    showToast,
    hideToast,
    showSuccess,
    showError,
    showInfo,
    ToastManager: () => <ToastManager messages={messages} onClose={hideToast} />
  }
}
